import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, ShieldAlert, Flame, X, Umbrella } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020' };
const FLAG = { ok: C.green, watch: C.amber, breach: C.red, unmeasured: C.slate };
const fmtM = (v) => v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;

export default function GeoExposurePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState('tiv');
  const [catOnly, setCatOnly] = useState(false);
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const openDrill = async (p) => {
    setDrill(p.postcode); setDrillData(null); setSel([]);
    try { const r = await api.get(`/analytics/geo-exposure/${p.postcode}/policies`); setDrillData(r.data); }
    catch (e) { toast.error('Could not load postcode policies'); setDrill(null); }
  };

  const referFac = async () => {
    setBusy(true);
    try {
      const r = await api.post('/analytics/geo-exposure/fac-referral', { policy_ids: sel, postcode: drill });
      toast.success(r.data.message);
      const rr = await api.get(`/analytics/geo-exposure/${drill}/policies`);
      setDrillData(rr.data); setSel([]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Referral failed'); }
    setBusy(false);
  };

  useEffect(() => {
    api.get('/analytics/geo-exposure').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  if (!data) return <div className="space-y-3"><Skeleton className="h-24 rounded-sm" /><Skeleton className="h-96 rounded-sm" /></div>;

  const rows = catOnly ? data.postcodes.filter(p => p.cat_zones.length > 0) : data.postcodes;
  const maxV = Math.max(...data.postcodes.map(p => p[metric]), 1);
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="geo-exposure-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Geo Exposure Heatmap</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>Postcode-level accumulation — circle size is {metric === 'tiv' ? 'total insured value' : 'written premium'}, colour is {s.limit_configured ? 'proximity to the aggregate limit' : 'unmeasured until an aggregate limit is set'}. Live book only.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.3)' }}>
            {[['tiv', 'TIV'], ['gwp', 'GWP']].map(([k, l]) => (
              <button key={k} onClick={() => setMetric(k)} data-testid={`geo-metric-${k}`} aria-pressed={metric === k}
                className="px-3 py-1.5 text-xs font-semibold"
                style={{ background: metric === k ? C.navy : '#fff', color: metric === k ? '#fff' : C.slate }}>{l}</button>
            ))}
          </div>
          <button onClick={() => setCatOnly(v => !v)} data-testid="geo-cat-toggle" aria-pressed={catOnly}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold"
            style={{ background: catOnly ? 'rgba(226,75,74,0.1)' : '#fff', color: catOnly ? C.red : C.slate, border: `1px solid ${catOnly ? 'rgba(226,75,74,0.4)' : 'rgba(136,150,168,0.3)'}` }}>
            <Flame className="w-3.5 h-3.5" /> CAT zones only
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[['Mapped postcodes', s.mapped_postcodes, C.navy],
          ['Total TIV mapped', fmtM(s.total_tiv), C.blue],
          ['CAT-exposed postcodes', s.cat_exposed_postcodes, C.amber],
          s.limit_configured
            ? [`Refers (≥${data.treaty.refer_pct}%)`, s.watch, C.amber]
            : ['Unmeasured', s.unmeasured, C.slate],
          s.limit_configured
            ? [`Refuses (≥${data.treaty.refuse_pct}%)`, s.breach, s.breach > 0 ? C.red : C.green]
            : ['Aggregate limit', 'not set', C.amber]].map(([l, v, color]) => (
          <Card key={l} className="p-3 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{l}</p>
            <p className="text-2xl font-light mt-0.5" style={{ color, fontVariantNumeric: 'tabular-nums' }} data-testid={`geo-stat-${String(l).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{v}</p>
          </Card>
        ))}
      </div>

      <Card className="px-4 py-2.5 rounded-sm bg-white" data-testid="geo-treaty-note"
        style={{ border: `1px solid ${s.limit_configured ? 'rgba(29,158,117,0.3)' : 'rgba(232,160,32,0.4)'}` }}>
        <p className="text-[11px] leading-relaxed" style={{ color: C.navy }}>
          <b style={{ color: s.limit_configured ? C.green : C.amber }}>
            {s.limit_configured ? 'Measured against the aggregate limit. ' : 'No aggregate limit configured. '}
          </b>
          {data.treaty.note}
          {!s.limit_configured && <> Set it at <b>Portfolio → Capacity Gate</b> and the bind door starts holding binds the same minute.</>}
        </p>
      </Card>

      <Card className="rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
        <MapContainer center={[-28.5, 145]} zoom={4} style={{ height: 520, width: '100%' }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          {rows.map(p => (
            <CircleMarker key={p.postcode} center={[p.lat, p.lng]}
              radius={Math.max(5, Math.sqrt(p[metric] / maxV) * 30)}
              pathOptions={{ color: FLAG[p.flag], fillColor: FLAG[p.flag], fillOpacity: 0.45, weight: p.cat_zones.length ? 2.5 : 1, dashArray: p.cat_zones.length ? '4 3' : null }}>
              <Popup>
                <div style={{ fontSize: 12, minWidth: 190 }}>
                  <b>{p.postcode} {p.state}</b> · {p.policies} policies<br />
                  GWP {fmtM(p.gwp)} · TIV {fmtM(p.tiv)}<br />
                  {p.fac_placed_tiv > 0 && <span style={{ color: C.green }}>Fac placed −{fmtM(p.fac_placed_tiv)} · retained {fmtM(p.retained_tiv)}<br /></span>}
                  PML (10%) {fmtM(p.pml)} — <b style={{ color: FLAG[p.flag] }}>
                    {p.treaty_utilisation_pct == null
                      ? `${p.pml_multiple_of_event_cover}× single-event cover`
                      : `${p.treaty_utilisation_pct}% of the aggregate limit`}</b>
                  {p.cat_zones.length > 0 && <><br /><span style={{ color: C.red }}>CAT: {p.cat_zones.join(', ')} ({p.cat_exposed} risks)</span></>}
                  <br /><button onClick={() => openDrill(p)} data-testid={`geo-popup-drill-${p.postcode}`}
                    style={{ marginTop: 6, background: C.navy, color: '#fff', border: 'none', borderRadius: 3, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    View policies →</button>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
        <div className="flex flex-wrap items-center gap-4 px-4 py-2 text-[11px]" style={{ color: C.slate, borderTop: '1px solid rgba(136,150,168,0.15)' }}>
          {(s.limit_configured
            ? [['ok', 'Within the limit'], ['watch', `Refers ≥${data.treaty.refer_pct}%`], ['breach', `Refuses ≥${data.treaty.refuse_pct}%`]]
            : [['unmeasured', 'No aggregate limit set — exposure shown, no verdict']]).map(([f, l]) => (
            <span key={f} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: FLAG[f] }} />{l}</span>
          ))}
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full border-2 border-dashed" style={{ borderColor: C.red }} />Dashed ring = CAT-zone exposure</span>
          <span className="ml-auto">
            {s.limit_configured
              ? `Aggregate limit ${fmtM(data.treaty.aggregate_limit_per_postcode)} per postcode`
              : `Single-event XoL cover ${fmtM(data.treaty.single_event_cover)}`} · PML at {data.treaty.pml_damage_ratio * 100}% damage ratio
          </span>
        </div>
      </Card>

      {drill && (
        <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(26,76,124,0.3)' }} data-testid="geo-drilldown">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
            <MapPin className="w-4 h-4" style={{ color: C.blue }} />
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Postcode {drill} — accumulation drilldown</h3>
            {drillData && (
              <span className="text-xs" style={{ color: C.slate }}>
                {drillData.summary.policies} policies · TIV {fmtM(drillData.summary.tiv)}
                {drillData.summary.fac_placed_tiv > 0 && <span style={{ color: C.green }}> · fac −{fmtM(drillData.summary.fac_placed_tiv)}</span>} ·
                <b style={{ color: FLAG[drillData.summary.flag] || C.slate }}>
                  {drillData.summary.treaty_utilisation_pct == null
                    ? ` ${drillData.summary.pml_multiple_of_event_cover}× single-event cover (retained)`
                    : ` ${drillData.summary.treaty_utilisation_pct}% of the aggregate limit (retained)`}</b>
              </span>
            )}
            {drillData && drillData.summary.tiv_to_shed > 0 && (
              <span className="px-2 py-1 rounded-sm text-[11px] font-semibold" style={{ background: 'rgba(226,75,74,0.08)', color: C.red }} data-testid="geo-drill-shed">
                Shed {fmtM(drillData.summary.tiv_to_shed)} TIV (or place fac) to get back under the refusal line
              </span>
            )}
            <button onClick={() => setDrill(null)} aria-label="Close drilldown" data-testid="geo-drill-close"
              className="ml-auto p-1.5 rounded hover:bg-[#F5F7FA]"><X className="w-4 h-4" style={{ color: C.slate }} /></button>
          </div>
          {!drillData ? <Skeleton className="h-32 m-4 rounded-sm" /> : (
            <>
              <div className="flex items-center gap-3 px-4 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)', background: '#FAFBFC' }}>
                <span className="text-[11px]" style={{ color: C.slate }}>
                  {sel.length > 0
                    ? <>{sel.length} selected · {fmtM(drillData.policies.filter(p => sel.includes(p.policy_id)).reduce((a, p) => a + (p.sum_insured || 0), 0))} TIV</>
                    : 'Select the largest risks and refer them for facultative placement to relieve the accumulation.'}
                </span>
                <button onClick={referFac} disabled={busy || sel.length === 0} data-testid="geo-refer-fac-btn"
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-40"
                  style={{ background: C.blue }}>
                  <Umbrella className="w-3.5 h-3.5" /> Refer {sel.length > 0 ? sel.length : ''} to facultative
                </button>
              </div>
              <div className="max-h-[380px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                    {['', 'Policy', 'Insured', 'Class', 'TIV', 'GWP', 'Expiry', 'CAT', 'Status'].map((h, i) => (
                      <th key={i} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>))}
                  </tr></thead>
                  <tbody>
                    {drillData.policies.map(p => {
                      const referred = !!p.fac_referral;
                      const checked = sel.includes(p.policy_id);
                      return (
                        <tr key={p.policy_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)', opacity: referred ? 0.65 : 1 }} data-testid={`geo-drill-policy-${p.policy_number}`}>
                          <td className="py-2 px-3">
                            <input type="checkbox" checked={checked} disabled={referred} aria-label={`Select ${p.business_name}`}
                              onChange={() => setSel(s => checked ? s.filter(x => x !== p.policy_id) : [...s, p.policy_id])}
                              data-testid={`geo-drill-check-${p.policy_number}`} />
                          </td>
                          <td className="py-2 px-3 font-mono" style={{ color: C.navy }}>{p.policy_number}</td>
                          <td className="py-2 px-3 font-medium max-w-[220px] truncate" style={{ color: C.navy }}>{p.business_name}</td>
                          <td className="py-2 px-3 capitalize" style={{ color: C.slate }}>{(p.class || '').replace(/_/g, ' ')}</td>
                          <td className="py-2 px-3 font-semibold" style={{ color: C.blue }}>{fmtM(p.sum_insured || 0)}</td>
                          <td className="py-2 px-3" style={{ color: C.navy }}>{fmtM(p.annual_premium || 0)}</td>
                          <td className="py-2 px-3" style={{ color: C.slate }}>{(p.expiry_date || '').slice(0, 10) || '—'}</td>
                          <td className="py-2 px-3" style={{ color: p.cat_zones?.length ? C.red : C.slate }}>{p.cat_zones?.join(', ') || '—'}</td>
                          <td className="py-2 px-3">
                            {referred && <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase" style={{ background: 'rgba(232,160,32,0.12)', color: '#B27516' }}>Fac referred</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="geo-top-table">
        <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <ShieldAlert className="w-3.5 h-3.5" style={{ color: C.amber }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Top accumulations</h3>
        </div>
        <table className="w-full text-xs">
          <thead><tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
            {['Postcode', 'Policies', 'GWP', 'TIV', 'PML', s.limit_configured ? 'Util of limit' : '× event cover', 'CAT zones'].map(h => (
              <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.slice(0, 12).map(p => (
              <tr key={p.postcode} onClick={() => openDrill(p)} title="View the policies driving this accumulation"
                className="border-b cursor-pointer hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`geo-row-${p.postcode}`}>
                <td className="py-2 px-3 font-mono font-semibold" style={{ color: C.navy }}><MapPin className="w-3 h-3 inline mr-1" style={{ color: C.slate }} />{p.postcode} {p.state}</td>
                <td className="py-2 px-3" style={{ color: C.navy }}>{p.policies}</td>
                <td className="py-2 px-3" style={{ color: C.navy }}>{fmtM(p.gwp)}</td>
                <td className="py-2 px-3 font-semibold" style={{ color: C.blue }}>{fmtM(p.tiv)}</td>
                <td className="py-2 px-3" style={{ color: C.slate }}>{fmtM(p.pml)}</td>
                <td className="py-2 px-3 font-semibold" style={{ color: FLAG[p.flag] }}>
                  {p.treaty_utilisation_pct == null ? `${p.pml_multiple_of_event_cover}×` : `${p.treaty_utilisation_pct}%`}
                </td>
                <td className="py-2 px-3" style={{ color: p.cat_zones.length ? C.red : C.slate }}>{p.cat_zones.join(', ') || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
